/**
 * «Хронум» у Экономиста (ТЗ AI-Economist-2026 ред. 2.1, п. 6.4, 6.8, 7.4): что и
 * когда пришло из Хронума, свод часов месяца по объектам и центрам затрат,
 * сопоставления «должность → категория ФОТ» и «площадка → центр затрат» с
 * комментариями и журналом правок, история загрузок. Читать — право чтения
 * Экономиста; сопоставления и «Обновить» — право записи; «ФОТ месяца» —
 * право-действие `econ.payroll.read` (суммы по должностям видят не все).
 */
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  useGetEconChronumStatusQuery,
  useListEconChronumPositionsQuery,
  useListEconChronumSitesQuery,
  useMeQuery,
} from '@shared/api';
import { useActionPermission } from '@entities/user';
import { useEconPermission } from '@entities/economic/types';
import { PAYROLL_CATEGORIES_FALLBACK } from '@entities/chronum';
import { TabPanel, Tabs, type TabItem } from '@widgets/FleetRegistry/ui';
import { HistoryPanel } from './HistoryPanel';
import { HoursPanel } from './HoursPanel';
import { PayrollPanel } from './PayrollPanel';
import { PositionsPanel } from './PositionsPanel';
import { SitesPanel } from './SitesPanel';
import { StatusHeader } from './StatusHeader';
import { useChronumParams, type ChronumTab } from './useChronumParams';
import { useChronumRefresh } from './useChronumRefresh';
import { Root } from './ui';

const TAB_PREFIX = 'chronum';

const TabsBar = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PanelBox = styled.div`
  padding-top: ${({ theme }) => theme.spacing(5)};
`;

export function ChronumView() {
  const { can_write } = useEconPermission();
  // Начисленное — чувствительные данные: вкладку показываем только с правом.
  const payrollAccess = useActionPermission('econ.payroll.read').allowed;
  const { data: me } = useMeQuery();
  const params = useChronumParams();
  // Статус выбранного месяца: табло шапки, пустой свод и идущая загрузка месяца.
  const status = useGetEconChronumStatusQuery(params.period, { refetchOnMountOrArgChange: 30 });
  const positions = useListEconChronumPositionsQuery();
  const sites = useListEconChronumSitesQuery();
  const refresh = useChronumRefresh();

  // Месяц уже загружается (коллега, другая вкладка, расписание) — следим за ходом.
  const periodLoad = status.currentData?.period_load ?? null;
  const { follow } = refresh;
  useEffect(() => {
    if (periodLoad?.status === 'running') follow(periodLoad);
  }, [periodLoad, follow]);

  const categories = status.data?.payroll_categories.length
    ? status.data.payroll_categories
    : PAYROLL_CATEGORIES_FALLBACK;
  const progressAccess = Boolean(me?.agents.some((a) => a.agent_code === 'progress' && a.can_read));

  const tabs = useMemo<readonly TabItem<ChronumTab>[]>(
    () => [
      { id: 'hours', label: 'Часы месяца' },
      ...(payrollAccess ? [{ id: 'payroll' as const, label: 'ФОТ месяца' }] : []),
      { id: 'positions', label: 'Должности', count: positions.data?.items.length },
      { id: 'sites', label: 'Площадки', count: sites.data?.items.length },
      { id: 'history', label: 'История загрузок' },
    ],
    [payrollAccess, positions.data, sites.data],
  );

  const { clearSite, openSite, openUnmapped } = params;
  const onFocusConsumed = useCallback(() => clearSite(), [clearSite]);

  return (
    <Root>
      <StatusHeader
        status={status.data}
        monthStatus={status.currentData}
        statusLoading={status.isLoading}
        statusError={status.isError ? status.error : null}
        onRetryStatus={() => void status.refetch()}
        period={params.period}
        currentPeriod={params.currentPeriod}
        onPeriod={params.setPeriod}
        canWrite={can_write}
        refresh={refresh}
      />

      <TabsBar>
        <Tabs
          items={tabs}
          value={params.tab}
          onChange={params.setTab}
          idPrefix={TAB_PREFIX}
          label="Разделы Хронума"
        />
      </TabsBar>

      <TabPanel idPrefix={TAB_PREFIX} id={params.tab}>
        <PanelBox>
          {params.tab === 'hours' && (
            <HoursPanel
              period={params.period}
              configured={status.data?.configured ?? false}
              statusLoading={status.isLoading}
              lastCredited={status.currentData?.last['hours:credited'] ?? null}
              categories={categories}
              canWrite={can_write}
              refresh={refresh}
              onOpenSite={openSite}
              onOpenUnmapped={openUnmapped}
            />
          )}
          {params.tab === 'payroll' && payrollAccess && (
            <PayrollPanel
              period={params.period}
              configured={status.data?.configured ?? false}
              statusLoading={status.isLoading}
              lastSnapshot={status.currentData?.last.labor_cost ?? null}
              categories={categories}
              canWrite={can_write}
              refresh={refresh}
              onOpenSite={openSite}
              onOpenUnmapped={openUnmapped}
            />
          )}
          {params.tab === 'positions' && (
            <PositionsPanel
              categories={categories}
              canWrite={can_write}
              unmappedOnly={params.unmappedOnly}
              onUnmappedOnly={params.setUnmappedOnly}
            />
          )}
          {params.tab === 'sites' && (
            <SitesPanel
              canWrite={can_write}
              attentionOnly={params.attentionOnly}
              onAttentionOnly={params.setAttentionOnly}
              focusSite={params.site}
              onFocusConsumed={onFocusConsumed}
              progressAccess={progressAccess}
            />
          )}
          {params.tab === 'history' && <HistoryPanel period={params.period} />}
        </PanelBox>
      </TabPanel>
    </Root>
  );
}
